import { z } from "zod";
import { doseLogSummarySchema } from "./dose-log-summary";
import { formFieldKindSchema, type FormFieldKind } from "./forms";
import { employeeRoleSchema } from "./roles";
import { transferPlanEligibility } from "./transfer-plan-choice";

// CAR-53: the intake (anamnes) form is a versioned, DB-stored, editable
// template — not a hardcoded code template. The shapes below are the shared
// contract: the API owns it, the admin portal edits drafts against it, and
// the mobile app renders the published version.
//
// Copy is carried inline as `{ sv, en }` rather than i18n keys, because the
// template content itself is now editable data — a clinician changing copy in
// the admin editor must not require an app release. The code-side evaluator
// still reads a fixed set of fields (see `evaluatorPinned`), which the draft
// guardrail protects.
//
// CAR-92: the same shape now backs a SECOND editable template — the weekly
// check-in (`key: "weekly_checkin"`). The check-in has no code evaluator, so
// none of its fields are `evaluatorPinned` and no draft guardrail applies; it
// is pure DB-backed content authored in the admin editor exactly like intake.
// `key` is therefore a free string keyed by template kind rather than the
// `"intake"` literal it used to be. The generic names below
// (`FormTemplateContent`, `formTemplateContentSchema`) are the canonical ones;
// the `Intake*` aliases are retained so existing call sites keep compiling.

export interface LocalizedText {
  sv: string;
  en: string;
}

export const localizedTextSchema = z.object({
  sv: z.string(),
  en: z.string(),
});

// TES-361 — AUTHORED patient-facing copy must carry Swedish. Since TES-362 the
// render path falls back en→sv (`localizedOr`), so an empty `en` degrades
// gracefully but an empty `sv` is truly blank on every surface. Schemas for
// authored copy (flow item content, articles, FAQ) use this variant; the
// generic `localizedTextSchema` above stays permissive on purpose — form
// TEMPLATE fields may legitimately be draft-empty while being authored.
export const svRequiredLocalizedTextSchema = z.object({
  sv: z.string().min(1, "Swedish copy (sv) is required — en may be empty"),
  en: z.string(),
});

// TES-1107 (Ö-327) — an option offered only when a named rule holds for the
// answers so far. Emelie: with shorter or unclear earlier treatment F-07 shows
// only the option with a coach, and the one without is never shown locked or
// disabled. A field-level `showWhen` can only branch on one
// answer's value; this rule needs the start date as well.
//
// A closed set of rules, like `formContextKeySchema`: a template names a rule,
// it cannot write one. Each rule is the SAME function the server enforces, so
// what the form offers and what the server accepts cannot drift apart.
//
// Presentation: the server keeps its own check (F-07's plan_not_eligible),
// which is what an app that predates this field meets. Such an app strips
// the unknown key and offers every option, exactly as before.
export const templateOptionRuleSchema = z.enum([
  // transferPlanEligibility: "ja" to twelve months AND a start date at least
  // twelve months back.
  "transfer_plan_eligible",
]);
export type TemplateOptionRule = z.infer<typeof templateOptionRuleSchema>;

export interface TemplateOptionCondition {
  rule: TemplateOptionRule;
}

export interface TemplateFieldOption {
  value: string;
  label: LocalizedText;
  /** TES-1107 — offered only when the rule holds; absent = always offered. */
  showWhen?: TemplateOptionCondition;
  /**
   * TES-1107 — this option's line of the field's help text. When every
   * option of a field carries one, the help shown is the lines of the
   * options on offer, so an option that is not offered is not explained
   * either. The field's own `help` stays the full text for older apps.
   */
  help?: LocalizedText;
  /**
   * TES-1605 — in a multi_select, an option that cannot be combined with
   * the others ("Inget av dessa"): choosing it clears them, and choosing
   * another clears it. Enforced by the app only. An older app ignores the
   * key, and the server keeps reading every other value sent with it, so a
   * red flag ticked beside it is still raised.
   */
  exclusive?: boolean;
}

export const templateFieldOptionSchema = z.object({
  value: z.string().min(1),
  label: localizedTextSchema,
  showWhen: z.object({ rule: templateOptionRuleSchema }).optional(),
  help: localizedTextSchema.optional(),
  exclusive: z.boolean().optional(),
});

// F-0x conditional questions — visibility ported from the bmi-quiz's
// showWhen but with multiple trigger values (the specs routinely branch on
// "Ja | Osäker"). A field with showWhen renders only when the parent answer
// matches: string parent → equality against any trigger; array parent →
// intersection. Hidden fields are never required and their answers are
// rejected server-side (a hidden answer can only come from a stale client).
export interface TemplateFieldCondition {
  /** Branch on another answer in the SAME submission. */
  parentKey?: string;
  triggerValues?: string[];
  /**
   * Branch on patient context resolved SERVER-SIDE from an earlier form.
   *
   * A form cannot see another form's answers, and re-asking the same clinical
   * question in the second form is how contradictory answers get created — so
   * the branch is delivered as context instead. The server resolves it from
   * the patient's own rows and never trusts a client-supplied value.
   *
   * Boolean by design: the field is visible when the flag is true. Anything
   * richer belongs in the form itself, where the patient can see and correct it.
   *
   * TES-1136 — may be combined with `parentKey` + `triggerValues`: BOTH must
   * hold. App 2.0.4/2.0.5 evaluate the context alone (see
   * `isTemplateFieldVisibleToLegacyClients`), so a combined condition only
   * ever shows those builds MORE, never less.
   */
  contextKey?: FormContextKey;
}

/**
 * Patient context a template may branch on. A closed set on purpose — a
 * template must not be able to reach into arbitrary server state.
 */
// TES-759 — `new_treatment` is the inverse of `prescription_transfer` and
// exists because the condition is boolean on purpose ("visible when the flag
// is true"). F-02's meeting questions must show for NEW treatments, i.e.
// exactly when the other flag is false, and a negation in the condition
// would have made every showWhen something the reader has to interpret both
// ways.
//
// TES-1116 (TES-1112 point 7) — F-02's conditional low-BMI section
// (F02_LOW_BMI_ASSESSMENT): NEW_TREATMENT with a computed F-01 BMI of
// 25,0–26,9. Resolved on the server only (`isLowBmiAssessment`). Appended
// last so `sectionTitleFor`, which takes the first true key with an
// override, keeps resolving the two older keys first.
//
// TES-1164 — F-02 v17's fallback for the two safety questions F-01 owns
// (type 1 diabetes, MTC/MEN 2): true when her latest F-01 has no answer to
// one of them, i.e. it was answered before F-01 v10 or in an app build that
// was served v9. Appended last, for the same reason as above.
//
// TES-1641 (TES-1600 S6, Ö-1002) — the optional care meeting splits
// NEW_TREATMENT in two, as `new_treatment`/`prescription_transfer` split the
// groups: `written_assessment` is a new patient on the no-meeting track
// (`careMeetingOptional` on, positive F-01, no meeting required, booked or
// held), and `care_meeting` is every other new patient. Two keys rather than
// one and its negation, for the reason TES-759 gives above. F-02 v26 asks
// "Vad vill du framför allt få hjälp med under mötet?" on `care_meeting` and
// heads the section as for a transfer on `written_assessment`. With the flag
// off, `care_meeting` equals `new_treatment` and `written_assessment` is never
// true. Appended last, for the same reason as above.
//
// App 2.0.4 handles a key it was built without: it does not zod-parse the
// context (`fetchFormContext` casts the JSON) or the template
// (`isTemplate` checks key/version/sections only), and
// `isTemplateFieldVisible` reads `context[key] === true`.
export const formContextKeySchema = z.enum([
  "prescription_transfer",
  "new_treatment",
  "low_bmi_assessment",
  "f01_safety_unanswered",
  "care_meeting",
  "written_assessment",
]);
export type FormContextKey = z.infer<typeof formContextKeySchema>;

/**
 * Sources a field may be seeded from (TES-508). Closed, like
 * {@link formContextKeySchema} — a template names a source, it can never point
 * at arbitrary server state.
 */
export const initialValueSourceSchema = z.enum([
  "current_weight",
  "height",
  // TES-524 (Ö-79) — F-01 Q12 / SUIT_DESIRED_OUTCOME,
  // carried into F-02 as the SAME data point: the option values are the
  // stable ids, so a copy change on either form breaks nothing.
  "desired_outcome",
  // TES-524 §2 (e2e 2026-09-06) — F-01's SUIT_REPRODUCTIVE_STATUS (TES-430),
  // asked again verbatim by F-02's pregnancy section ten minutes later.
  // Seeded as the intake's own option id (a fixed map, both sides stable ids).
  "reproductive_status",
  // TES-728 — F-07 v3's blood pressure / resting pulse, offered from her
  // latest MeasurementSession when it is at most seven days old (the same
  // "Godkänn"/"Ändra" reuse as weight). All three come from ONE session, so a
  // form that seeds one seeds all or none.
  "blood_pressure_systolic",
  "blood_pressure_diastolic",
  "resting_pulse",
  // Simon 2026-09-21, on Zeinab's F-02 walkthrough ("Since I already answered
  // that I'm on an ongoing treatment, this should come up: Which
  // treatment?") — the SAME principle as Ö-159/Ö-179: a patient is not asked
  // cold to establish what her own track already states.
  //
  // Unlike the sources above this one is not read out of an earlier ANSWER.
  // It is derived from the patient group (`resolvePatientGroup`), which is why
  // it has no `initialSources` provenance entry: there is no form, question id
  // or submission date to point a "hämtat från …" note at. The seeded value is
  // an ordinary editable answer she can correct, not a carried-over summary,
  // so it is never paired with `lockedWhenSeeded`.
  "weight_medicine_history",
  // TES-985 (Ö-265) — F-05's optional waist and blood-pressure questions,
  // offered from "Dina värden" when the reading is at most seven days old.
  // `waist` is the latest waist; `blood_pressure_measured_on` is the day of
  // the SAME occasion the three blood-pressure sources above come from, so
  // she approves the reading together with the date it was taken.
  "waist",
  "blood_pressure_measured_on",
  // TES-1256 (Ella 2.1 slice J) — F-05's MONTH_LAST_DOSE_DATE and
  // MONTH_ADHERENCE, answered from her dose log when the log is sufficient
  // (dose-log-summary.ts says when). Resolved only for a build at or after
  // CLIENT_CONTRACT_CHANGES.f05DoseSummary: an older build seeds a
  // single-select silently, and Ö-444 forbids reusing an answer she did not
  // actively confirm.
  "last_dose_date",
  "dose_adherence",
  // TES-1418 — F-04's WEEK_DOSE_ADHERENCE, on the same terms, resolved only
  // for a build at or after CLIENT_CONTRACT_CHANGES.f04DoseSummary. Its own
  // source: F-04 asks since her previous check-in and answers ja/nej, F-05
  // since her previous F-05 and answers alla/missat_en/missat_flera.
  "week_dose_adherence",
  // TES-1451 (Ö-728) — F-04's and F-05's weight, offered from her newest
  // current weight in the measurement series when it was measured at most
  // seven days ago. She approves it (her answer then refers to that reading,
  // no new weight) or changes it (a new reading). Resolved only for a build
  // at or after CLIENT_CONTRACT_CHANGES.checkinWeightReuse.
  "fresh_weight",
  // TES-1449 (Ö-712) — the day (YYYY-MM-DD, tenant zone) of the SAME
  // occasion `fresh_weight` comes from, so she approves the weight together
  // with the day it was measured, as for blood pressure. On the same terms
  // as `fresh_weight`.
  "weight_measured_on",
]);
export type InitialValueSource = z.infer<typeof initialValueSourceSchema>;

/**
 * The patient's own numbers, resolved server-side, for fields that carry an
 * `initialFrom`. `null` means "no source" — the field renders empty, which is
 * a normal state and never an error.
 */
export const initialValuesSchema = z.object({
  current_weight: z.number().nullable(),
  height: z.number().nullable(),
  // TES-524 — the patient's F-01 goal(s) as option values. Optional so an
  // app built after this still reads a server from before it.
  desired_outcome: z.array(z.string()).nullable().optional(),
  // TES-524 — F-01's reproductive status as the intake field's option value.
  reproductive_status: z.string().nullable().optional(),
  // TES-728 — the ≤7-day reading for F-07 v3. Optional for the same
  // old-app/new-server reason as above.
  blood_pressure_systolic: z.number().nullable().optional(),
  blood_pressure_diastolic: z.number().nullable().optional(),
  resting_pulse: z.number().nullable().optional(),
  // Simon 2026-09-21 — the intake field's own option value ("ja") for a
  // prescription-transfer patient, null for everyone else. Optional for the
  // same old-app/new-server reason as the rest.
  weight_medicine_history: z.string().nullable().optional(),
  // TES-985 — the ≤7-day waist, and the day (YYYY-MM-DD, tenant zone) of the
  // ≤7-day blood-pressure occasion. Optional for the same reason as above.
  waist: z.number().nullable().optional(),
  blood_pressure_measured_on: z.string().nullable().optional(),
  // TES-1256 — from her dose log (YYYY-MM-DD, tenant zone) and MONTH_ADHERENCE's
  // option value; null when the log is insufficient. Optional for the same
  // reason as above.
  last_dose_date: z.string().nullable().optional(),
  dose_adherence: z.string().nullable().optional(),
  // TES-1418 — WEEK_DOSE_ADHERENCE's option value from her dose log; null
  // when the log is insufficient.
  week_dose_adherence: z.string().nullable().optional(),
  // TES-1451 — her ≤7-day weight in kg; null without one or for a build
  // that does not show it. Optional for the same reason as above.
  fresh_weight: z.number().nullable().optional(),
  // TES-1449 — the day of that weight; null with it.
  weight_measured_on: z.string().nullable().optional(),
});
export type InitialValues = z.infer<typeof initialValuesSchema>;

/**
 * TES-524 — where a seeded value came from, so the patient can be told
 * ("från din kartläggning 3 september") and the doctor sees source, form
 * version and timestamp rather than a bare value.
 */
export const initialValueProvenanceSchema = z.object({
  templateKey: z.string(),
  questionId: z.string(),
  templateVersion: z.number().int().nullable(),
  submittedAt: z.string(),
});
export type InitialValueProvenance = z.infer<
  typeof initialValueProvenanceSchema
>;

/**
 * TES-985 — the "Dina värden" occasion a suggested reading comes from, so the
 * app can say where and when it was measured and the patient can approve,
 * change or skip it. Separate from {@link initialValueProvenanceSchema}: that
 * one names a form answer, this one a measurement occasion.
 */
export const initialReadingSchema = z.object({
  sessionId: z.string(),
  measuredAt: z.string(),
  source: z.string(),
  sourceSurface: z.string().nullable(),
});
export type InitialReading = z.infer<typeof initialReadingSchema>;

export const formContextSchema = z.object({
  /**
   * The patient is on the prescription-transfer journey.
   *
   * TES-760 — read through `resolvePatientGroup`, not from F-01's answers
   * directly: a doctor can move a case to a transfer after the fact, and that
   * stored decision wins over the derivation. A context that read the answers
   * itself would hand a transferred patient F-02A's meeting questions while
   * every other surface showed her as a transfer.
   */
  prescription_transfer: z.boolean(),
  /**
   * The inverse, carried explicitly because the condition is boolean by
   * design. F-02's meeting questions are visible for NEW treatments, and a
   * negation in the condition would make every `showWhen` something the
   * reader has to interpret in two directions.
   *
   * Optional so an older client that predates TES-759 still parses a newer
   * server's context.
   */
  new_treatment: z.boolean().optional(),
  /**
   * TES-1116 — NEW_TREATMENT and a computed F-01 BMI of 25,0–26,9: F-02 shows
   * its low-BMI section. Always false on the transfer track. Optional so a
   * client or server from before TES-1116 still parses the other's context;
   * absent reads as false, which hides the section.
   */
  low_bmi_assessment: z.boolean().optional(),
  /**
   * TES-1164 — her latest F-01 did not ask the type 1 diabetes or the MTC/MEN 2
   * question (or she has no F-01), so F-02 asks them. Optional so a client or
   * server from before TES-1164 still parses the other's context; absent
   * reads as false, which hides the fallback questions.
   */
  f01_safety_unanswered: z.boolean().optional(),
  /**
   * TES-1641 — NEW_TREATMENT on the meeting track, and NEW_TREATMENT on the
   * optional care meeting's no-meeting track (see formContextKeySchema).
   * Optional so a client or server from before TES-1641 still parses the
   * other's context; absent reads as false.
   */
  care_meeting: z.boolean().optional(),
  written_assessment: z.boolean().optional(),
  /**
   * Optional so a newer app stays compatible with a server that predates
   * TES-508 — an absent map simply seeds nothing.
   */
  initialValues: initialValuesSchema.optional(),
  /**
   * TES-987 (Ö-266) — whether she has a personal coach right now
   * (`coachAccessForPatient`: the plan, never the clinical phase). F-04
   * groups its questions by who reads them, and the groups differ with and
   * without a coach. Optional so an older server's context still parses; an
   * absent value reads as "active", the same default the server uses.
   */
  coachAccess: z.enum(["active", "inactive"]).optional(),
  /** Provenance per seeded source, where the server has one. */
  initialSources: z
    .object({
      desired_outcome: initialValueProvenanceSchema.optional(),
      reproductive_status: initialValueProvenanceSchema.optional(),
    })
    .optional(),
  /**
   * TES-985 — the occasions behind the seeded readings, null when there is
   * no reading from the last seven days. Optional so an app built before it
   * still parses a newer server, and the other way round.
   */
  initialReadings: z
    .object({
      waist: initialReadingSchema.nullable(),
      blood_pressure: initialReadingSchema.nullable(),
      // TES-1451 — the occasion behind `fresh_weight`. Optional: a server
      // before it, or a build that does not show it, sends none.
      weight: initialReadingSchema.nullable().optional(),
    })
    .optional(),
  /**
   * TES-1256 — what her dose log says about F-05's two dose questions, and
   * whether that is enough to show it for confirm/correct instead of asking.
   * Sent only to a build at or after CLIENT_CONTRACT_CHANGES.f05DoseSummary;
   * `weekAdherence` (F-04, TES-1418) only at or after f04DoseSummary.
   */
  doseSummary: doseLogSummarySchema.optional(),
});
export type FormContext = z.infer<typeof formContextSchema>;

export const templateFieldConditionSchema = z
  .object({
    parentKey: z.string().min(1).optional(),
    triggerValues: z.array(z.string().min(1)).min(1).optional(),
    contextKey: formContextKeySchema.optional(),
  })
  // At least one branching source, and an answer branch is the pair
  // parentKey + triggerValues. TES-1136 — both sources together mean AND
  // (F-02 v15: Kirill's lab values need the low-BMI context AND "Ja" to
  // having the results). There is no "which one wins": both must hold.
  .refine(
    (c) =>
      (c.parentKey === undefined) === (c.triggerValues === undefined) &&
      (c.contextKey !== undefined || c.parentKey !== undefined),
    {
      message:
        "showWhen needs parentKey+triggerValues, contextKey, or both (AND)",
    },
  );

// F-07 — hard ceilings the template author cannot exceed. A template is
// editable content, so without these a clinician could author a field that
// accepts a 500 MB file and the API would honour it. Per-field config narrows
// these; it can never widen them.
// 10 MB decoded — picked from the CONTENT, not the transport: what arrives
// here is a phone photo of a prescription (2–4 MB) or a scanned multi-page
// lab PDF, and 10 MB clears both with room to spare. This is THE cap; the
// route-scoped parser in main.ts is only the outer guard and is derived from
// this number (FORM_UPLOAD_MAX_BODY_BYTES below) so it can never drift below
// it. A payload cap the parser rejects first is a cap that never produces
// upload_too_large — the patient would get express's bare 413 instead.
export const FORM_UPLOAD_MAX_BYTES = 10 * 1024 * 1024;

// What the route-scoped JSON parser in main.ts must admit for the cap
// above to be reachable: base64 inflates by 4/3, plus room for the JSON
// envelope around it. Derived rather than written as a second literal so
// the transport limit cannot drift below the payload limit — a cap the
// parser rejects first is a cap that never produces upload_too_large.
export const FORM_UPLOAD_MAX_BODY_BYTES =
  Math.ceil((FORM_UPLOAD_MAX_BYTES * 4) / 3) + 64 * 1024;
export const FORM_UPLOAD_MAX_COUNT = 10;

// F-07 — per-field upload config for `kind: "file_upload"`.
//
// Deliberately NOT carrying a `required` flag: TemplateField.required already
// says whether the field must be answered, and two flags for one question is
// how "obligatoriskt receptunderlag" ends up optional on one surface and
// required on another. A required upload field means `min 1 file`; the schema
// deriver enforces that.
export interface TemplateFieldUpload {
  /** Exact mime types the client may send. Closed set — no wildcards. */
  allowedMimeTypes: string[];
  /** Per-FILE decoded byte cap. Re-checked server-side on upload. */
  maxBytes: number;
  /** How many files this one field accepts. */
  maxCount: number;
}

export const templateFieldUploadSchema = z.object({
  allowedMimeTypes: z.array(z.string().min(1)).min(1),
  maxBytes: z.number().int().positive().max(FORM_UPLOAD_MAX_BYTES),
  maxCount: z.number().int().positive().max(FORM_UPLOAD_MAX_COUNT),
});

// TES-940 — the allowed side of today for a `date` field's picker.
export const templateFieldDateRangeSchema = z.enum(["past", "future", "any"]);
export type TemplateFieldDateRange = z.infer<
  typeof templateFieldDateRangeSchema
>;

export interface TemplateField {
  // Dot-path into the answers shape, e.g. "currentWeightKg",
  // "history.diabetes". The schema deriver builds nested Zod objects from it.
  key: string;
  kind: FormFieldKind;
  label: LocalizedText;
  help?: LocalizedText;
  unit?: string;
  required: boolean;
  // single_select / multi_select only.
  options?: TemplateFieldOption[];
  // numeric only.
  numeric?: { min?: number; max?: number; integer?: boolean };
  // TES-396 — scale_1_5 only: per-step word labels (index 0 = value 1).
  // Presentation only; the stored answer stays numeric.
  stepLabels?: LocalizedText[];
  // file_upload only. Absent on a file_upload field = no constraints declared,
  // which the deriver treats as the platform ceilings.
  upload?: TemplateFieldUpload;
  // F-07: this answer goes stale. After a draft's 7-day
  // kompletteringsperiod lapses the patient must re-confirm these fields
  // before submitting — the stored answer is KEPT and shown back to them,
  // never cleared. Weight and current dose are the motivating cases.
  timeSensitive?: boolean;
  // TES-940 — date only: which side of today the picker allows. Absent means
  // "past" (capped at today), which is what every date field asked for before
  // this existed — last dose, treatment start, sample date. "future" is for a
  // date that has not happened yet, such as when a prescription expires.
  // Presentation only: the schema deriver accepts any valid calendar date
  // regardless, so an older app that ignores this still submits cleanly.
  dateRange?: TemplateFieldDateRange;
  // Conditional visibility; absent = always visible.
  showWhen?: TemplateFieldCondition;
  // Client-resolved read-only prefill rendered above the field (e.g. the
  // patient's current dose from /me for a confirm question). Never answered,
  // never validated server-side.
  prefill?: "current_dose";
  // TES-508 — seed the field with the patient's own value, EDITABLE. Distinct
  // from `prefill`, which renders read-only text above an empty input: this
  // fills the input itself, and whatever the patient leaves there is what gets
  // submitted. The template names a source; the server resolves it per patient
  // into `FormContext.initialValues` (the template is shared and client-cached,
  // so a patient's number must never travel inside it).
  initialFrom?: InitialValueSource;
  // TES-524 — with a seed present the field is a LOCKED summary of what the
  // patient already answered elsewhere (the "same data point" rule): shown,
  // submitted as-is, not editable here. Without a seed it is an ordinary
  // field — a patient who never took F-01 answers the question herself.
  lockedWhenSeeded?: boolean;
  // TES-1247 (Ella 2.1 slice A) — the canonical question this field asks
  // (question-register.v1.ts). A bound field's copy and answer scale are the
  // register's: saving or publishing a template whose bound field differs is
  // refused, and the admin editor locks them. Server and admin metadata
  // only; an app ignores it. A string rather than an enum on purpose: a
  // stored template must keep parsing if an id is ever retired.
  questionId?: string;
  // TES-1247 — for a question asked as several fields (BLOOD_PRESSURE_PULSE:
  // systolic, diastolic, pulse, date), which part this field is.
  questionPart?: string;
  // The code evaluator reads this field — its `key` and `kind` are locked.
  // The draft guardrail rejects any edit that re-keys, removes, or changes
  // the kind of a pinned field.
  evaluatorPinned: boolean;
}

export const templateFieldSchema = z.object({
  key: z.string().min(1),
  kind: formFieldKindSchema,
  label: localizedTextSchema,
  help: localizedTextSchema.optional(),
  unit: z.string().optional(),
  required: z.boolean(),
  options: z.array(templateFieldOptionSchema).optional(),
  numeric: z
    .object({
      min: z.number().optional(),
      max: z.number().optional(),
      integer: z.boolean().optional(),
    })
    .optional(),
  // TES-396 — scale_1_5 only: per-step word labels (index 0 = value 1).
  // Presentation only; the stored answer stays numeric.
  stepLabels: z.array(localizedTextSchema).length(5).optional(),
  // F-07 — file_upload config + the staleness marker the 7-day
  // kompletteringsperiod reads.
  upload: templateFieldUploadSchema.optional(),
  timeSensitive: z.boolean().optional(),
  dateRange: templateFieldDateRangeSchema.optional(),
  showWhen: templateFieldConditionSchema.optional(),
  prefill: z.enum(["current_dose"]).optional(),
  initialFrom: initialValueSourceSchema.optional(),
  lockedWhenSeeded: z.boolean().optional(),
  // TES-1247 — the register binding; see TemplateField.questionId.
  questionId: z.string().min(1).optional(),
  questionPart: z.string().min(1).optional(),
  evaluatorPinned: z.boolean(),
});

// Shared visibility rule — the ONE implementation the server deriver, the
// mobile renderer and the validity helpers all use, so "hidden" can never
// mean different things on different surfaces.
export function isTemplateFieldVisible(
  field: TemplateField,
  getAnswer: (key: string) => unknown,
  context?: FormContext,
): boolean {
  const cond = field.showWhen;
  if (!cond) return true;

  if (cond.contextKey !== undefined) {
    // Missing context is a wiring bug, not a hidden field. Returning false
    // would silently drop a field the server may consider required, and the
    // patient would be blocked from submitting with no way to see why.
    if (context === undefined) {
      throw new Error(
        `field "${field.key}" branches on context "${cond.contextKey}" but no context was supplied`,
      );
    }
    if (context[cond.contextKey] !== true) return false;
    // TES-1136 — a context-only condition ends here; a combined one must
    // also match the answer below (AND).
    if (cond.parentKey === undefined) return true;
  }

  const parent = getAnswer(cond.parentKey!);
  if (parent === undefined || parent === null) return false;
  if (Array.isArray(parent)) {
    return cond.triggerValues!.some((v) => parent.includes(v));
  }
  return cond.triggerValues!.includes(String(parent));
}

/**
 * TES-1136 — the visibility app 2.0.4 and 2.0.5 compute. Their bundled
 * `isTemplateFieldVisible` (since TES-429, #314) returns the context flag
 * as soon as a condition has a `contextKey` and never looks at a
 * `parentKey` beside it. For a combined condition they therefore show the
 * field whenever the context holds, a superset of what 2.0.6 shows.
 *
 * The server must accept what those builds send: an answer to a field they
 * showed is never "an answer for a hidden field". The deriver rejects hidden
 * answers by this rule, and demands required ones by the current one, which
 * is the stricter of the two, so neither build can be blocked.
 */
export function isTemplateFieldVisibleToLegacyClients(
  field: TemplateField,
  getAnswer: (key: string) => unknown,
  context?: FormContext,
): boolean {
  const cond = field.showWhen;
  if (cond?.contextKey === undefined || cond.parentKey === undefined) {
    return isTemplateFieldVisible(field, getAnswer, context);
  }
  return isTemplateFieldVisible(
    { ...field, showWhen: { contextKey: cond.contextKey } },
    getAnswer,
    context,
  );
}

const OPTION_RULES: Record<
  TemplateOptionRule,
  (getAnswer: (key: string) => unknown, now: Date) => boolean
> = {
  transfer_plan_eligible: (getAnswer, now) =>
    transferPlanEligibility(
      {
        pt_twelve_months_total: getAnswer("pt_twelve_months_total"),
        pt_treatment_start: getAnswer("pt_treatment_start"),
        pt_treatment_started_at: getAnswer("pt_treatment_started_at"),
      },
      now,
    ).status === "eligible",
};

/** TES-1107 — whether an option is offered, given the answers so far. */
export function isTemplateOptionVisible(
  option: TemplateFieldOption,
  getAnswer: (key: string) => unknown,
  now: Date,
): boolean {
  return option.showWhen
    ? OPTION_RULES[option.showWhen.rule](getAnswer, now)
    : true;
}

/**
 * TES-1107 — the field as the patient is shown it: only the options on offer,
 * and, when every option carries its own help line, only their lines. The
 * same object when nothing about it is conditional.
 */
export function withVisibleOptions(
  field: TemplateField,
  getAnswer: (key: string) => unknown,
  now: Date,
): TemplateField {
  const options = field.options;
  if (!options || !options.some((o) => o.showWhen !== undefined)) return field;
  const offered = options.filter((o) =>
    isTemplateOptionVisible(o, getAnswer, now),
  );
  const lines = offered.map((o) => o.help);
  const help = options.every((o) => o.help !== undefined)
    ? {
        sv: lines.map((l) => l!.sv).join("\n"),
        en: lines.map((l) => l!.en).join("\n"),
      }
    : field.help;
  return { ...field, options: offered, ...(help ? { help } : {}) };
}

/**
 * The heading to show for a section, given the patient's context.
 *
 * Falls back to `title` whenever there is no override for her variant, or no
 * context at all — a heading is never worth failing a render over, which is
 * why this does NOT throw the way `isTemplateFieldVisible` does. Getting a
 * heading wrong shows the wrong words; getting a field's visibility wrong
 * changes what is asked and what is required.
 */
export function sectionTitleFor(
  section: Pick<TemplateSection, "title" | "titleByContext">,
  context?: FormContext,
): LocalizedText {
  if (!section.titleByContext || context === undefined) return section.title;
  for (const key of formContextKeySchema.options) {
    if (context[key] === true) {
      const override = section.titleByContext[key];
      if (override) return override;
    }
  }
  return section.title;
}

/** Does this template branch on patient context anywhere? */
export function templateNeedsContext(content: {
  sections: { fields: TemplateField[] }[];
}): boolean {
  return content.sections.some((s) =>
    s.fields.some((f) => f.showWhen?.contextKey !== undefined),
  );
}

export interface TemplateSection {
  id: string;
  title: LocalizedText;
  description?: LocalizedText;
  fields: TemplateField[];
  /**
   * TES-800 (Ö-177) — a heading that differs per patient variant.
   *
   * Emelie 2026-09-18: F-02's confirmation section is headed "Inför mötet"
   * for new treatment and "Inför läkarens bedömning" for a prescription
   * transfer, who books no meeting. The questions inside it are already
   * per-variant via each field's `showWhen`; what could not be expressed was
   * the HEADING, because it belongs to the section rather than to any field.
   *
   * Deliberately an override of the TITLE and nothing else. Giving sections a
   * `showWhen` was the other candidate and is worse: the two variants share
   * `med_anything_else` and `med_confirmation`, so hiding whole sections
   * would mean duplicating those field keys across two sections — and the
   * deriver keys the schema by field, so the duplicates would collide. It
   * would also need an AND with the field-level `showWhen` that already sits
   * on `med_main_question`, which the condition shape cannot express.
   *
   * A missing entry falls back to `title`, so every existing template and
   * every renderer that ignores this keeps working unchanged.
   */
  titleByContext?: Partial<Record<FormContextKey, LocalizedText>>;
}

export const templateSectionSchema = z.object({
  id: z.string().min(1),
  title: localizedTextSchema,
  description: localizedTextSchema.optional(),
  fields: z.array(templateFieldSchema),
  // TES-800 — per-variant heading; see TemplateSection.titleByContext.
  titleByContext: z
    .record(formContextKeySchema, localizedTextSchema)
    .optional(),
});

// TES-293 (spec §11) — review metadata every form template carries. These are
// DECLARATIVE for now: they describe how submissions should be routed and
// gated; the enforcement wiring lands with the journey state machine (J1,
// TES-279 — blocksNextState) and the generic review items (J6, TES-282 —
// approvalRequired/requiredRole). Persisted inside the template content Json,
// versioned with it; templates saved before TES-293 get per-kind defaults
// from the API's FormTemplateRegistry.
export const FORM_TYPES = [
  "medical",
  "coach",
  "wellness",
  "monthly",
  "weekly",
] as const;
export const formTypeSchema = z.enum(FORM_TYPES);
export type FormType = z.infer<typeof formTypeSchema>;

export const formReviewMetaSchema = z.object({
  formType: formTypeSchema,
  // Which staff role reviews submissions of this form.
  requiredRole: employeeRoleSchema,
  // Submissions need an explicit review/approval before downstream effects.
  approvalRequired: z.boolean(),
  // An unreviewed submission blocks the patient's journey-state progression.
  blocksNextState: z.boolean(),
  // A summary is generated from the answers (AI/deterministic evaluator).
  summaryGenerated: z.boolean(),
  // The evaluator computes risk flags/hard-stops for this form.
  riskScoring: z.boolean(),
  // The (approved) summary is shown to the patient.
  patientVisibleSummary: z.boolean(),
  // Submissions are part of the patient's health history record.
  storedInHealthHistory: z.boolean(),
});
export type FormReviewMeta = z.infer<typeof formReviewMetaSchema>;

export interface FormTemplateContent {
  // Template kind, e.g. "intake" or "weekly_checkin". One row family per key
  // per tenant, versioned independently (DB unique on [tenantId, key, version]).
  key: string;
  version: number;
  title: LocalizedText;
  description: LocalizedText;
  sections: TemplateSection[];
  // TES-293 — optional in the schema (pre-existing rows lack it); the API
  // backfills per-kind defaults on read so consumers can rely on it.
  meta?: FormReviewMeta;
}

export const formTemplateContentSchema = z.object({
  key: z.string().min(1),
  version: z.number().int().positive(),
  title: localizedTextSchema,
  description: localizedTextSchema,
  sections: z.array(templateSectionSchema),
  meta: formReviewMetaSchema.optional(),
});

// The clinician editor view: live published content + the single in-flight
// draft (or null).
export interface FormTemplateEditorView {
  published: FormTemplateContent;
  draft: FormTemplateContent | null;
}

// Back-compat aliases (CAR-53 names). The intake template is one kind of form
// template; these keep existing imports working after the CAR-92 generalisation.
export type IntakeTemplateContent = FormTemplateContent;
export const intakeTemplateContentSchema = formTemplateContentSchema;
export type IntakeTemplateEditorView = FormTemplateEditorView;

// ---------------------------------------------------------------------------
// F-07 — form file uploads
// ---------------------------------------------------------------------------
//
// The bytes live in their own encrypted, tenant-scoped row (FormUpload); the
// form ANSWER only ever carries the upload's id. That split is deliberate:
// a submission's answers JSON is copied into exports, review payloads and
// audit metadata, and a base64 PDF riding along in all of those is exactly
// the leak we're avoiding. It also lets a patient attach files across several
// autosaves without the draft answers growing to megabytes.

/** The effective limits for a file_upload field: its own config, clamped. */
export function resolveUploadConfig(
  field: Pick<TemplateField, "upload">,
): TemplateFieldUpload {
  const cfg = field.upload;
  return {
    allowedMimeTypes: cfg?.allowedMimeTypes ?? [],
    maxBytes: Math.min(
      cfg?.maxBytes ?? FORM_UPLOAD_MAX_BYTES,
      FORM_UPLOAD_MAX_BYTES,
    ),
    maxCount: Math.min(
      cfg?.maxCount ?? FORM_UPLOAD_MAX_COUNT,
      FORM_UPLOAD_MAX_COUNT,
    ),
  };
}

/**
 * The answer stored for a `file_upload` field: the ids of the uploads the
 * patient attached, in the order they attached them.
 */
export const formUploadAnswerSchema = z.array(z.string().uuid());
export type FormUploadAnswer = z.infer<typeof formUploadAnswerSchema>;

// Base64 with no data-URI prefix and no whitespace. Buffer.from is lenient —
// it silently drops invalid characters — so a mangled body must be rejected
// here rather than stored as truncated garbage. Same rule as uploadPhotoSchema.
export const uploadFormFileSchema = z.object({
  /** Which template the upload belongs to, e.g. "intake". */
  templateKey: z.string().min(1).max(120),
  /** Which `file_upload` field it answers — its dot-path key. */
  fieldKey: z.string().min(1).max(200),
  /** Original filename, shown back to the patient and to the doctor. */
  filename: z.string().min(1).max(255),
  mimeType: z.string().min(1).max(120),
  dataBase64: z.string().min(1),
});
export type UploadFormFileInput = z.infer<typeof uploadFormFileSchema>;

// Metadata-only DTO. Listing NEVER carries the bytes; those come from the
// dedicated per-upload endpoint with the stored Content-Type.
export const formUploadMetaSchema = z.object({
  id: z.string().uuid(),
  templateKey: z.string(),
  fieldKey: z.string(),
  filename: z.string(),
  mimeType: z.string(),
  byteSize: z.number().int(),
  createdAt: z.string(),
});
export type FormUploadMeta = z.infer<typeof formUploadMetaSchema>;

// ---------------------------------------------------------------------------
// F-07 — server-side form drafts
// ---------------------------------------------------------------------------
//
// "Påbörjat onboarding-/receptövertagsunderlag har en aktiv kompletteringsperiod på 7 dagar. Efter detta krävs återbekräftelse av tidskänsliga uppgifter, men befintliga svar och uppladdningar ska inte raderas automatiskt."
//
// So the period is a REVIEW gate, not a retention policy. Nothing expires,
// nothing is swept: once `completionPeriodEndsAt` passes, the fields marked
// `timeSensitive` come back flagged for re-confirmation and everything the
// patient entered — answers and uploads alike — is still there.

export const FORM_DRAFT_COMPLETION_PERIOD_DAYS = 7;

export const formDraftStatusSchema = z.enum(["draft", "submitted"]);
export type FormDraftStatus = z.infer<typeof formDraftStatusSchema>;

export const saveFormDraftSchema = z.object({
  templateKey: z.string().min(1).max(120),
  templateVersion: z.number().int().positive(),
  /**
   * Partial answers — NOT validated against the template schema. A draft is
   * by definition incomplete; running submission validation here would reject
   * every autosave until the last field is filled, which is the opposite of
   * what a draft is for. Validation happens once, at submit.
   */
  answers: z.record(z.unknown()),
  /** How far the patient has read; presentation state, restored on resume. */
  sectionIndex: z.number().int().min(0).max(200).optional(),
});
export type SaveFormDraftInput = z.infer<typeof saveFormDraftSchema>;

export const formDraftSchema = z.object({
  id: z.string().uuid(),
  templateKey: z.string(),
  templateVersion: z.number().int().positive(),
  answers: z.record(z.unknown()),
  sectionIndex: z.number().int(),
  status: formDraftStatusSchema,
  startedAt: z.string(),
  lastSavedAt: z.string(),
  /** startedAt + FORM_DRAFT_COMPLETION_PERIOD_DAYS. */
  completionPeriodEndsAt: z.string(),
  /** Has the 7-day period lapsed? Derived server-side from the clock. */
  needsReconfirmation: z.boolean(),
  /**
   * The `timeSensitive` field keys the patient must re-confirm before this
   * draft can be submitted. Empty while the period is still active. Their
   * ANSWERS are still present in `answers` — this is a confirm list, not a
   * clear list.
   */
  reconfirmKeys: z.array(z.string()),
  /**
   * TES-459 — the F-07 sections a doctor's komplettering reopens (template
   * order, confirmation last), or null when no komplettering is open for
   * this template. An empty array is a komplettering that reopens nothing
   * (`other_medical` only — answered in the chat). `reconfirmKeys` is
   * already restricted to these sections when non-null.
   */
  completionSectionIds: z.array(z.string()).nullable(),
  /** Uploads attached to this draft so far, metadata only. */
  uploads: z.array(formUploadMetaSchema),
  /** When this draft last produced a submission (null if it never has). */
  lastSubmittedAt: z.string().nullable(),
  lastSubmissionId: z.string().uuid().nullable(),
});
export type FormDraft = z.infer<typeof formDraftSchema>;

export const submitFormDraftSchema = z.object({
  templateKey: z.string().min(1).max(120),
  submittedInLocale: z.enum(["sv", "en"]),
  /**
   * The keys the patient just re-confirmed. Must cover `reconfirmKeys` when
   * the completion period has lapsed, or the submit is refused — the point of
   * the period is that stale time-sensitive data never reaches the doctor
   * unchallenged.
   */
  reconfirmedKeys: z.array(z.string()).optional(),
  prescriptionCycleId: z.string().uuid().optional(),
});
export type SubmitFormDraftInput = z.infer<typeof submitFormDraftSchema>;
